import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronDown, ChevronUp, FileText, X, Calendar, Download } from 'lucide-react';
import { SkeletonTableRows, Empty } from '../ui/kit';
import { berlinerBetriebswoche, betriebstagSchluessel } from '../lib/zeit';

const fmtEur = (v) => `€${(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Cent-Ganzzahlen der Abrechnung. `|| 0` faengt auch das negative Null ab, das sonst
// als „€-0,00" erschiene.
const fmtCent = (cents) => fmtEur((Number(cents) || 0) / 100);
const gedreht = (cents) => (Number(cents) || 0) === 0 ? 0 : -Number(cents);
const fmtDay = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || '–';
  const [year, month, day] = iso.split('-');
  return `${day}.${month}.${year}`;
};

// Betriebliche Kennzahlen wechseln erst um 04:00 Europe/Berlin.
export const tagesString = (date) => betriebstagSchluessel(date);

export function defaultRange(now = new Date()) {
  return berlinerBetriebswoche(now);
}

const PERIODEN_STATUS = {
  calculated: 'Berechnet',
  ready: 'Freigegeben',
  exported: 'An Bankdatei übergeben',
  paid: 'Ausgezahlt',
};

const SummaryRow = ({ label, value, bold, color, expandable, open, onToggle, children }) => (
  <>
    <div data-zeile={label} onClick={expandable ? onToggle : undefined}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: bold ? 'none' : '1px solid #f3f4f6', cursor: expandable ? 'pointer' : 'default' }}>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 500, color: '#374151' }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: bold ? 800 : 600, color: color || '#111' }}>
        {value}{expandable && (open ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
      </span>
    </div>
    {expandable && open && <div style={{ paddingLeft: 14 }}>{children}</div>}
  </>
);

// EINNAHMEN-UBER-001 — Aufklappzustände leben im Seiten-State, damit ein Datenneuladen
// (Zeitraumwechsel, erneuter GET) die geöffneten Positionen NICHT zuklappt.
const DEFAULT_OPEN = {
  partnerumsatz: true,
  korrekturen: true,
  zahlweise: true,
  zusatz: true,
  buchungen: false,
  d_partnerumsatz: true,
  d_korrekturen: true,
  d_zahlweise: true,
  d_zusatz: true,
};

const chipStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8,
  background: '#f3f4f6', border: 'none', fontSize: 13, fontWeight: 700,
  color: disabled ? '#6b7280' : '#374151', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});

export default function Earnings() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(defaultRange());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null); // selected driver for drilldown
  const [open, setOpen] = useState(DEFAULT_OPEN);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const toggle = useCallback((key) => setOpen((o) => ({ ...o, [key]: !o[key] })), []);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/partners/earnings', { params: { from: range.from, to: range.to } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // P1-5: Diese Seite laedt KEINEN laufenden Kontostand mehr. `open_projection_cents`
  // liest ausdruecklich nur Buchungen OHNE Periode (financeLedger.js:806-813) und laesst
  // freigegebene, noch nicht bezahlte Auszahlungen weg (`unpaidPayoutCents`, :832) —
  // der vollstaendige Stand heisst dort `balanceCents` (:833). Unter der Ueberschrift
  // „Verbindliche Abrechnung / Aus dem Wochenabschluss" war die Zahl damit doppelt
  // falsch beschriftet: sie kommt gerade NICHT aus einem Wochenabschluss und gilt auch
  // nicht fuer das ganze Konto. Sie steht auf der Banking-Seite, deren Unterzeile sie
  // richtig als offenen, keiner Periode zugeordneten Stand benennt (Balance.js:108).

  const s = data?.summary || {};
  // Die Abrechnungswochen kommen aus derselben Antwort, ausgewaehlt und freigegeben
  // vom Server (`settlement_period_match: 'overlaps_selected_range'`,
  // backend/src/routes/partners.js). Der Browser waehlt hier NICHTS nach — das
  // Freigabe-Tor `periodReadAuthority(...).deliverable` darf nicht zweimal existieren.
  // `null` heißt „nicht beantwortet" und ist NICHT dasselbe wie „keine Abrechnungswoche":
  // sonst behauptete ein Netzfehler eine leere Woche.
  const perioden = Array.isArray(data?.settlement_periods) ? data.settlement_periods : null;
  const drivers = useMemo(
    () => (data?.drivers || []).filter(d => d.name?.toLowerCase().includes(search.toLowerCase())),
    [data, search],
  );

  const pageCount = Math.max(1, Math.ceil(drivers.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const visible = drivers.slice(safePage * pageSize, safePage * pageSize + pageSize);
  useEffect(() => { setPage(0); }, [search, pageSize, data]);

  // Zählwerte existieren nur in neueren DTOs. Fehlen sie, wird die Gruppe gar nicht
  // gezeigt — eine erfundene 0 wäre eine Aussage, die die Route nicht macht.
  const buchungsZahl = s.ledger_entry_count ?? s.event_count ?? null;

  // Bericht laden: exakt die Werte der Route, keine Umrechnung, keine ergänzte Null.
  const downloadCsv = () => {
    const cols = [
      ['Fahrername', d => d.name || ''],
      ['Buchungszeilen', d => d.ledger_entry_count ?? d.event_count ?? ''],
      ['Korrekturbuchungen', d => d.correction_entry_count ?? ''],
      ['Partnerumsatz', d => d.partner_driver_gross ?? ''],
      ['Kundeneinzug Online', d => d.online_revenue ?? ''],
      ['Kundeneinzug Bar', d => d.cash_revenue ?? ''],
      ['Trinkgeld (Information)', d => d.tip_information ?? ''],
      ['Bonus (Partner an Fahrer)', d => d.bonus_information ?? ''],
      ['Korrekturen', d => d.corrections ?? ''],
      ['Kontobewegung bei VOOGO', d => d.ledger_effect ?? ''],
    ];
    const rows = [cols.map(c => c[0]), ...drivers.map(d => cols.map(c => c[1](d)))];
    const csv = rows.map(r => r.map(x => `"${x}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `einnahmen-${range.from}_${range.to}.csv`;
    a.click();
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          {data?.partner_name && (
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563', marginBottom: 2 }}>{data.partner_name}</div>
          )}
          <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -1 }}>Einnahmen</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Ein Zeitraumfeld statt zweier loser Datumsfelder — beide Grenzen bleiben setzbar. */}
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setPickerOpen(v => !v)} aria-label="Zeitraum wählen"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: '#f3f4f6', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#374151', border: 'none', cursor: 'pointer' }}>
              <Calendar size={15} /> {fmtDay(range.from)} – {fmtDay(range.to)}
              {pickerOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {pickerOpen && (
              <>
                <div onClick={() => setPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 120 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 121, background: '#fff', borderRadius: 12, padding: 16, minWidth: 260, boxShadow: '0 10px 30px rgba(0,0,0,0.16)' }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <label style={{ flex: 1, fontSize: 12, color: '#4b5563', fontWeight: 700 }}>
                      Von
                      <input type="date" aria-label="Zeitraum von" value={range.from}
                        onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
                        style={{ display: 'block', width: '100%', marginTop: 6, padding: '8px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13, color: '#111' }} />
                    </label>
                    <label style={{ flex: 1, fontSize: 12, color: '#4b5563', fontWeight: 700 }}>
                      Bis
                      <input type="date" aria-label="Zeitraum bis" value={range.to}
                        onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
                        style={{ display: 'block', width: '100%', marginTop: 6, padding: '8px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13, color: '#111' }} />
                    </label>
                  </div>
                  <button type="button" onClick={() => { setRange(defaultRange()); }}
                    style={{ marginTop: 12, width: '100%', padding: '8px 10px', borderRadius: 8, background: '#f3f4f6', border: 'none', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>
                    Diese Woche
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={downloadCsv} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#f3f4f6', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#374151', border: 'none', cursor: 'pointer' }}>
            <Download size={15} /> Bericht laden
          </button>
          <button onClick={() => navigate('/settlements')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#f3f4f6', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#374151', border: 'none', cursor: 'pointer' }}>
            <FileText size={15} /> Zur verbindlichen Abrechnung
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 28, alignItems: 'start' }}>
        <div>
          {/* Informativer Block. Er trägt NIE das Wort „Saldo" und nie einen Rechenoperator —
              die Positionen ergeben den Endbetrag nicht immer, weil `amount_cents` das
              EINGEZOGENE Onlinegeld nimmt, nicht das ausgewiesene. */}
          <div data-finance-block="buchungen">
            <div style={{ background: '#f3f4f6', borderRadius: 14, padding: 22, marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Kontobewegung bei VOOGO</div>
              <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -1 }}>{fmtEur(s.ledger_effect)}</div>
              <div style={{ fontSize: 12, color: '#4b5563', marginTop: 8 }}>Information – keine Rechnung und keine Auszahlung</div>
              <div style={{ fontSize: 12, color: '#4b5563', marginTop: 6, lineHeight: 1.45 }}>
                Plus heißt: VOOGO schuldet dir. Minus heißt: du schuldest VOOGO. Bargeld ist nicht enthalten — das liegt bereits beim Fahrer.
              </div>
            </div>
            <div style={{ padding: '8px 4px' }}>
              {/* Zwei GETRENNTE Aufklappblöcke. Vorher hingen „Kundeneinzug Online/Bar"
                  und die Fahrpreis-Aufschlüsselung unter derselben Zeile: das Buch
                  erzwingt onlineCents + cashCents === grossCents
                  (backend/src/lib/financeLedger.js:927), damit standen mit
                  Provision + Servicegebühr = (Online + Bar) − Partnerumsatz die Operanden
                  und ihr Ergebnis in EINEM Block untereinander. Woraus der Umsatz
                  entsteht und wie der Kunde gezahlt hat sind verschiedene Fragen. */}
              <Umsatzzeile bucket={s} offen={open.partnerumsatz} umschalten={() => toggle('partnerumsatz')} />
              {s.correction_entry_count != null ? (
                <SummaryRow label="Korrekturen" value={fmtEur(s.corrections)}
                  expandable open={open.korrekturen} onToggle={() => toggle('korrekturen')}>
                  <SummaryRow label="davon Korrekturbuchungen" value={s.correction_entry_count} />
                </SummaryRow>
              ) : (
                <SummaryRow label="Korrekturen" value={fmtEur(s.corrections)} />
              )}
              <SummaryRow label="Wie der Kunde gezahlt hat"
                expandable open={open.zahlweise} onToggle={() => toggle('zahlweise')}>
                <SummaryRow label="Kundeneinzug Online" value={fmtEur(s.online_revenue)} />
                <SummaryRow label="Kundeneinzug Bar" value={fmtEur(s.cash_revenue)} />
              </SummaryRow>
              <SummaryRow label="Zusatzinformationen" expandable open={open.zusatz} onToggle={() => toggle('zusatz')}>
                <SummaryRow label="Trinkgeld – Information" value={fmtEur(s.tip_information)} />
                <SummaryRow label="Bonus – Partner → Fahrer" value={fmtEur(s.bonus_information)} />
              </SummaryRow>
              {buchungsZahl != null && (
                <SummaryRow label="Buchungszeilen" value={buchungsZahl}
                  expandable open={open.buchungen} onToggle={() => toggle('buchungen')}>
                  {/* `event_count` zaehlt JEDE Endbuchung einer Fahrt, also auch
                      Stornogebühr und Nichtantritt (backend/src/lib/financeLedger.js:14,
                      `TERMINAL_RIDE_EVENT_TYPES`). „davon Fahrtabschlüsse" behauptete
                      abgeschlossene Fahrten — und stand direkt unter der
                      Aufschlüsselung, die genau das widerlegt. */}
                  {s.event_count != null && <SummaryRow label="davon abgeschlossene Fahrten, Storno und Nichtantritt" value={s.event_count} />}
                  {s.correction_entry_count != null && <SummaryRow label="davon Korrekturbuchungen" value={s.correction_entry_count} />}
                </SummaryRow>
              )}
              <div style={{ borderTop: '2px solid #111', marginTop: 4 }}>
                <SummaryRow label="Kontobewegung bei VOOGO" value={fmtEur(s.ledger_effect)} bold />
              </div>
            </div>
          </div>

          <Abrechnungsblock perioden={perioden} range={range} />
        </div>

        {/* Driver earnings table */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800 }}>Fahrer-Einnahmen</h2>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Fahrername" aria-label="Fahrername"
              style={{ width: 240, padding: '10px 16px', borderRadius: 24, border: 'none', background: '#f3f4f6', fontSize: 14 }} />
          </div>
          <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table className="vg-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {/* Der Kopf wird ZWEIZEILIG umbrochen, nicht gekürzt: zwei Namen für dieselbe
                  Zahl wären schlimmer als ein langer Kopf. */}
              {['Fahrername', 'Buchungszeilen', 'Partnerumsatz', 'Kundeneinzug Online', 'Kundeneinzug Bar', ['Kontobewegung ', 'bei VOOGO'], ''].map((h, i) => (
                <th key={i} style={{ padding: '10px 12px', fontSize: 12, color: '#4b5563', fontWeight: 700, textAlign: i === 0 ? 'left' : 'right' }}>
                  {Array.isArray(h)
                    ? h.map((zeile, j) => <span key={j} style={{ display: 'block' }}>{zeile}</span>)
                    : h}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {loading && <SkeletonTableRows rows={5} cols={7} />}
              {!loading && drivers.length === 0 && <tr><td colSpan={7}><Empty icon={FileText} title="Keine Buchungen im Zeitraum" hint="Zeitraum erweitern oder Abrechnungshistorie öffnen." /></td></tr>}
              {!loading && visible.map(d => (
                <tr key={d.id} onClick={() => setDetail(d)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px', fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#4b5563' }}>{d.name?.charAt(0)}</div>
                      {d.name}
                    </div>
                  </td>
                  <td style={{ padding: '12px', fontSize: 13, textAlign: 'right' }}>{d.ledger_entry_count ?? d.event_count ?? 0}</td>
                  <td style={{ padding: '12px', fontSize: 13, textAlign: 'right', fontWeight: 700 }}>{fmtEur(d.partner_driver_gross)}</td>
                  <td style={{ padding: '12px', fontSize: 13, textAlign: 'right' }}>{fmtEur(d.online_revenue)}</td>
                  <td style={{ padding: '12px', fontSize: 13, textAlign: 'right' }}>{fmtEur(d.cash_revenue)}</td>
                  <td style={{ padding: '12px', fontSize: 13, textAlign: 'right', fontWeight: 700 }}>{fmtEur(d.ledger_effect)}</td>
                  <td style={{ padding: '12px', textAlign: 'right' }}><ChevronRight size={16} color="#6b7280" /></td>
                </tr>
              ))}
            </tbody>
          </table></div>

          {!loading && drivers.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <select aria-label="Zeilen pro Seite" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                style={{ padding: '7px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>
                {[10, 25, 50].map(n => <option key={n} value={n}>{n} Zeilen</option>)}
              </select>
              <button type="button" onClick={() => setPage(0)} disabled={safePage === 0} style={chipStyle(safePage === 0)}>
                <ChevronsLeft size={14} /> Erste
              </button>
              <button type="button" onClick={() => setPage(p => Math.max(0, Math.min(p, pageCount - 1) - 1))} disabled={safePage === 0} style={chipStyle(safePage === 0)}>
                <ChevronLeft size={14} /> Zurück
              </button>
              <button type="button" onClick={() => setPage(p => Math.min(pageCount - 1, Math.min(p, pageCount - 1) + 1))} disabled={safePage >= pageCount - 1} style={chipStyle(safePage >= pageCount - 1)}>
                Weiter <ChevronRight size={14} />
              </button>
              <span style={{ fontSize: 12, color: '#4b5563', fontWeight: 600 }}>
                Seite {safePage + 1} von {pageCount} · {drivers.length} Fahrer
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Drilldown drawer */}
      {detail && <EarningsDetail driver={detail} onClose={() => setDetail(null)} open={open} toggle={toggle} />}
    </div>
  );
}

// B5 — woraus der Partnerumsatz entsteht. Die Route liefert die Gruppen als
// `partner_driver_gross_by_event`; fehlt das Feld, wird NICHTS gezeigt (eine erfundene
// Null wäre eine Aussage, die die Route nicht macht). Gruppen mit 0 bleiben weg.
export function aufschluesselungGruppen(bucket) {
  const by = bucket?.partner_driver_gross_by_event;
  if (!by) return [];
  return [
    ['aus abgeschlossenen Fahrten', by.completed],
    ['aus Stornogebühren', by.cancellation_fee],
    ['aus Nichtantritt', by.no_show_fee],
  ].filter(([, wert]) => Number(wert || 0) !== 0);
}

export function Fahrpreisaufschluesselung({ bucket }) {
  const gruppen = aufschluesselungGruppen(bucket);
  if (gruppen.length === 0) return null;
  return (
    <>
      <div style={{ padding: '10px 0 2px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Woraus der Partnerumsatz entsteht</div>
        {/* Gegenbuchungen mindern diese Gruppen NICHT — sonst wiese eine stornierte
            Fahrt hier dauerhaft ihren vollen Betrag aus. Die Zeile „Korrekturen"
            steht deshalb unmittelbar darunter. */}
        <div style={{ fontSize: 11, color: '#4b5563' }}>vor Korrekturen</div>
      </div>
      {gruppen.map(([label, wert]) => (
        <SummaryRow key={label} label={label} value={fmtEur(wert)} />
      ))}
    </>
  );
}

// Der Partnerumsatz klappt NUR auf, wenn es wirklich etwas aufzuklappen gibt: ohne
// `partner_driver_gross_by_event` (aelteres Backend) haette die Zeile sonst einen leeren
// Aufklappblock. Seite und Fahrer-Auszug benutzen dasselbe Bauteil — zwei Abschriften
// waeren wieder zwei Wahrheiten.
export function Umsatzzeile({ bucket, offen, umschalten }) {
  const wert = fmtEur(bucket?.partner_driver_gross);
  if (aufschluesselungGruppen(bucket).length === 0) {
    return <SummaryRow label="Partnerumsatz" value={wert} bold />;
  }
  return (
    <SummaryRow label="Partnerumsatz" value={wert} bold expandable open={offen} onToggle={umschalten}>
      <Fahrpreisaufschluesselung bucket={bucket} />
    </SummaryRow>
  );
}

// B4 — der verbindliche Block aus der bereits servergeprüften Einnahmen-Antwort, optisch
// abgesetzt. Er trägt NIE das Wort „Kontobewegung". Nur hier stehen Rechenzeichen,
// weil nur hier die Kette immer aufgeht:
//   Anfangssaldo + Bewegung dieser Woche − Zahlungsdienstleisterkosten
//     = Auszahlungsbetrag + Schlusssaldo
// (backend/src/lib/financeLedger.js:1835-1838).
export function Abrechnungsblock({ perioden, range }) {
  return (
    <div data-finance-block="abrechnung" style={{ marginTop: 26, border: '2px solid #111', borderRadius: 14, padding: 18 }}>
      <div style={{ fontSize: 16, fontWeight: 800 }}>Verbindliche Abrechnung</div>
      <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4 }}>Aus dem Wochenabschluss</div>

      {perioden === null && (
        <div style={{ fontSize: 13, color: '#374151', marginTop: 14 }}>
          Der Wochenabschluss konnte nicht geladen werden.
        </div>
      )}
      {perioden !== null && perioden.length === 0 && (
        <div style={{ fontSize: 13, color: '#374151', marginTop: 14, lineHeight: 1.5 }}>
          Für diesen Zeitraum gibt es noch keinen Wochenabschluss.
        </div>
      )}
      {(perioden || []).map((periode) => (
        <Abrechnungswoche key={periode.id} periode={periode} range={range} />
      ))}

      <div style={{ fontSize: 11, color: '#4b5563', marginTop: 16, lineHeight: 1.45 }}>
        Die Abrechnungswoche beginnt montags um 04:00 Uhr (Berliner Zeit). Fahrten zwischen 00:00 und 04:00 Uhr am Montag gehören noch zur Vorwoche.
      </div>
    </div>
  );
}

// Jedes Rechenzeichen steht IM WERT — keines mehr in einer eigenen Spalte. Vorher stand
// das Minus der Gebühren am Operator und das Minus des Saldos in der Zahl: derselbe Strich
// bedeutete zweierlei. So haelt es auch der verbindliche Beleg, der die Gebühr als
// NEGATIVE Zahl druckt (backend/src/lib/financeDocuments.js:13-18,231).
const mitVorzeichen = (cents) => {
  const zahl = Number(cents) || 0;
  if (zahl <= 0) return fmtCent(zahl); // „€0,00" bzw. „€-12,40" — unveraendertes Hausformat
  return `€+${(zahl / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const Kettenzeile = ({ label, value, hinweis, bold }) => (
  <div data-zeile={label} style={{ padding: '9px 0', borderBottom: '1px solid #f3f4f6' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 500, color: '#374151' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600, color: '#111' }}>{value}</span>
    </div>
    {hinweis && <div style={{ fontSize: 11, color: '#4b5563', marginTop: 3, lineHeight: 1.45 }}>{hinweis}</div>}
  </div>
);

function Abrechnungswoche({ periode, range }) {
  const vonTag = betriebstagSchluessel(periode.period_start);
  const bisTag = betriebstagSchluessel(new Date(Date.parse(periode.period_end) - 60_000));
  // Beschriftet wird IMMER mit den eigenen Grenzen der Woche, nie mit dem gewählten Zeitraum.
  const ueberhang = (vonTag && vonTag < range.from) || (bisTag && bisTag > range.to);
  const anfang = Number(periode.carry_in_cents) || 0;
  const ausgezahlt = Number(periode.payout_cents) || 0;
  // carry_out wird als POSITIVE Schuld gespeichert und kommt als NEGATIVER carry_in
  // zurueck. Der Schlusssaldo ist deshalb −carry_out; die Null wird angezeigt.
  const schluss = gedreht(periode.carry_out_cents);
  // Die Ergebniszahl wird NICHT im Browser gerechnet. Das Buch setzt
  // payout = max(0, netto) und carry_out = max(0, −netto) (financeLedger.js:1836-1838):
  // hoechstens einer der beiden ist ungleich null, also ist das Ergebnis der von null
  // verschiedene SERVERWERT — ausgewaehlt, nicht addiert. Sind wider Erwarten beide
  // ungleich null, ist die Woche uneindeutig und es wird keine Summe behauptet.
  const eindeutig = ausgezahlt === 0 || schluss === 0;
  const zusammen = ausgezahlt !== 0 ? ausgezahlt : schluss;
  return (
    <div data-abrechnungswoche={vonTag} style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 800 }}>
        Abrechnungswoche {periode.period_label}
        <span style={{ fontWeight: 600, color: '#4b5563' }}> · {PERIODEN_STATUS[periode.state] || periode.state}</span>
      </div>
      {/* Ein Anfangssaldo kann strukturell nie positiv sein — er ist der gedrehte
          Übertrag der Vorwoche (financeLedger.js:1835). Bei 0 steht ein Satz. */}
      <Kettenzeile label="Anfangssaldo"
        value={anfang === 0 ? 'aus der Vorwoche steht nichts offen' : mitVorzeichen(anfang)} />
      <Kettenzeile label="Bewegung dieser Woche" value={mitVorzeichen(periode.ledger_effect_cents)} />
      <Kettenzeile label="Fahrten" value={String(periode.ride_count ?? '–')} />
      <Kettenzeile label="Bar" value={mitVorzeichen(periode.cash_cents)} />
      <Kettenzeile label="Online" value={mitVorzeichen(periode.online_cents)} />
      {/* Der verbindliche Beleg nennt dieses Feld so, und der Beleg ist das Blatt, das der
          Unternehmer abheftet (backend/src/lib/financeDocuments.js:212-214,230-231).
          „Kartengebühren" war zusätzlich zu eng: enthalten sind auch die Kosten aus
          Guthabenkäufen. */}
      <Kettenzeile label="Zahlungsdienstleisterkosten"
        value={mitVorzeichen(-(Number(periode.psp_cost_cents) || 0))}
        hinweis={`Was der Zahlungsdienstleister für den Einzug berechnet. Karte/Stripe ${periode.payment_provider_costs?.stripe?.transaction_count || 0} · ${fmtCent(periode.payment_provider_costs?.stripe?.fee_cents)}; PayPal ${periode.payment_provider_costs?.paypal?.transaction_count || 0} · ${fmtCent(periode.payment_provider_costs?.paypal?.fee_cents)}.`} />
      <div style={{ borderTop: '2px solid #111' }}>
        <Kettenzeile label="= ergibt zusammen" bold
          value={eindeutig ? mitVorzeichen(zusammen) : '–'}
          hinweis={eindeutig
            ? 'aufgeteilt in:'
            : 'Auszahlungsbetrag und Schlusssaldo sind beide ungleich null — diese Woche ergibt keine eindeutige Summe.'} />
      </div>
      <Kettenzeile label="Auszahlungsbetrag" value={mitVorzeichen(ausgezahlt)} bold />
      <Kettenzeile label="Schlusssaldo" value={mitVorzeichen(schluss)} bold />
      {ueberhang && (
        <div style={{ fontSize: 11, color: '#4b5563', marginTop: 8, lineHeight: 1.45 }}>
          Die Woche reicht über deinen Zeitraum hinaus; anteilig lässt sich eine Abrechnungswoche nicht aufteilen.
        </div>
      )}
    </div>
  );
}

function EarningsDetail({ driver, onClose, open, toggle }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '92vw', background: '#fff', zIndex: 201, padding: 28, overflowY: 'auto', boxShadow: '-4px 0 30px rgba(0,0,0,0.1)' }}>
        <button onClick={onClose} aria-label="Schließen" style={{ position: 'absolute', top: 20, right: 20, background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#4b5563' }}>{driver.name?.charAt(0)}</div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{driver.name}</div>
        </div>

        <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 14 }}>
          Informative Zuordnung aus nachträglich unveränderbaren Buchungen – keine Fahrer- oder Partnerauszahlung.
        </div>

        <Umsatzzeile bucket={driver} offen={open.d_partnerumsatz} umschalten={() => toggle('d_partnerumsatz')} />
        {driver.correction_entry_count != null ? (
          <SummaryRow label="Korrekturen" value={fmtEur(driver.corrections)}
            expandable open={open.d_korrekturen} onToggle={() => toggle('d_korrekturen')}>
            <SummaryRow label="davon Korrekturbuchungen" value={driver.correction_entry_count} />
          </SummaryRow>
        ) : (
          <SummaryRow label="Korrekturen" value={fmtEur(driver.corrections)} />
        )}
        <SummaryRow label="Wie der Kunde gezahlt hat"
          expandable open={open.d_zahlweise} onToggle={() => toggle('d_zahlweise')}>
          <SummaryRow label="Kundeneinzug Online" value={fmtEur(driver.online_revenue)} />
          <SummaryRow label="Kundeneinzug Bar" value={fmtEur(driver.cash_revenue)} />
        </SummaryRow>
        <SummaryRow label="Zusatzinformationen" expandable open={open.d_zusatz} onToggle={() => toggle('d_zusatz')}>
          <SummaryRow label="Trinkgeld – Information" value={fmtEur(driver.tip_information)} />
          <SummaryRow label="Bonus – Partner → Fahrer" value={fmtEur(driver.bonus_information)} />
        </SummaryRow>
        <div style={{ borderTop: '2px solid #111', marginTop: 4 }}>
          <SummaryRow label="Kontobewegung bei VOOGO" value={fmtEur(driver.ledger_effect)} bold />
        </div>

        {/* B7 — der Fahrer-Auszug bekommt NICHTS Neues: keine Fahrten, keine Kilometer,
            keine datierten Korrekturzeilen, kein zweiter Datenaufruf. Nur Zählwerte. */}
        <div style={{ marginTop: 18 }}>
          <SummaryRow label="Buchungszeilen" value={driver.ledger_entry_count ?? driver.event_count ?? 0} />
          {driver.correction_entry_count != null && (
            <SummaryRow label="Korrekturbuchungen" value={driver.correction_entry_count} />
          )}
        </div>
      </div>
    </>
  );
}
